import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronLeft, ChevronRight, HelpCircle, Lightbulb } from 'lucide-react'
import PopupCloseButton from 'components/PopupCloseButton'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent } from 'components/ui/dialog'
import { cn } from 'lib/utils'
import {
	fieldPrimer,
	primerPages,
	type PrimerCallout,
	type PrimerPlatform,
	type PrimerPage,
} from 'content/help/fieldPrimer'

/**
 * Where the reader had got to, so leaving the app does not lose their place.
 *
 * **This is not a convenience.** Following this primer means leaving Safari for
 * Settings twice and coming back, and iOS evicts a backgrounded tab under
 * memory pressure. A dialog holding its position only in React state drops the
 * reader back at step one at the exact moment they did as they were told, which
 * is the worst possible time to punish them.
 *
 * Cleared on finish, so a reader who completes it starts fresh next time rather
 * than reopening onto the last page.
 */
const STORAGE_KEY = 'mdt_field_primer_progress'

interface StoredProgress {
	page?: number
	platform?: PrimerPlatform
}

/** Storage throws in a private window, and a help dialog is never worth a crash. */
const readProgress = (): StoredProgress => {
	try {
		return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {}
	} catch {
		return {}
	}
}

const writeProgress = (value: StoredProgress) => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
	} catch {
		/* Private browsing, or storage disabled. The dialog still works. */
	}
}

const clearProgress = () => {
	try {
		window.localStorage.removeItem(STORAGE_KEY)
	} catch {
		/* As above. */
	}
}

/**
 * A guess, never a decision. The reader can always switch, because the guess is
 * wrong for anyone reading this on a desktop to set up a phone in their hand,
 * which is one of the two situations this document exists for.
 */
const guessPlatform = (): PrimerPlatform =>
	/iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android'

const CALLOUT_STYLE: Record<PrimerCallout['kind'], { wrap: string; icon: typeof Lightbulb }> = {
	warning: { wrap: 'border-warning/40 bg-warning/10', icon: AlertTriangle },
	tip: { wrap: 'border-primary/30 bg-primary/5', icon: Lightbulb },
	why: { wrap: 'border-gray-200 bg-gray-50', icon: HelpCircle },
}

const Callout = ({ callout }: { callout: PrimerCallout }) => {
	const { wrap, icon: Icon } = CALLOUT_STYLE[callout.kind]
	return (
		<div className={cn('flex gap-3 rounded-lg border p-3', wrap)}>
			<Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" aria-hidden="true" />
			<div className="min-w-0">
				<p className="text-sm font-semibold text-gray-900">{callout.title}</p>
				<p className="mt-1 text-sm leading-relaxed text-gray-600">{callout.text}</p>
			</div>
		</div>
	)
}

/** One page of the walkthrough. Split out so the paging logic stays readable. */
const PrimerPageBody = ({ page, platform }: { page: PrimerPage; platform: PrimerPlatform }) => {
	// A step with instructions for both phones shows only the one being read.
	// Showing both doubles the length of the page and asks the reader to filter,
	// which is exactly the work a guide is supposed to do for them.
	const actions = (page.actions ?? []).filter(a => a.platform === platform || a.platform === 'all')

	return (
		<div className="space-y-4">
			{page.body?.map((paragraph, i) => (
				<p key={i} className="text-sm leading-relaxed text-gray-600">
					{paragraph}
				</p>
			))}

			{page.list && (
				<ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
					{page.list.map(item => (
						<li key={item.label} className="px-3 py-2">
							<p className="text-sm font-semibold text-gray-900">{item.label}</p>
							<p className="text-sm text-gray-500">{item.hint}</p>
						</li>
					))}
				</ul>
			)}

			{actions.map(group => (
				<div key={group.platform}>
					<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
						{group.label}
					</p>
					<ol className="space-y-2">
						{group.items.map((item, i) => (
							<li key={i} className="flex gap-3">
								<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
									{i + 1}
								</span>
								<span className="pt-0.5 text-sm leading-relaxed text-gray-700">{item}</span>
							</li>
						))}
					</ol>
				</div>
			))}

			{page.callouts?.map((callout, i) => <Callout key={i} callout={callout} />)}
		</div>
	)
}

/** The troubleshooting table, shown as the final page. */
const TroublePage = () => (
	<div className="space-y-3">
		<p className="text-sm leading-relaxed text-gray-600">
			Six things that go wrong, and what each one actually is.
		</p>
		{fieldPrimer.troubleshooting.map(row => (
			<div key={row.symptom} className="rounded-lg border border-gray-200 p-3">
				<p className="text-sm font-semibold text-gray-900">{row.symptom}</p>
				<p className="mt-1 text-sm text-gray-500">{row.cause}</p>
				<p className="mt-2 text-sm leading-relaxed text-gray-700">{row.fix}</p>
			</div>
		))}
	</div>
)

export interface FieldPrimerDialogProps {
	open: boolean
	onClose: () => void
}

/**
 * The field-app primer, as a walkthrough you can follow on the phone you are
 * setting up.
 *
 * **Not the existing driver.js tour, and not for want of trying.**
 * `dashboardTour.ts` spotlights elements in the desktop sidebar, which is the
 * right shape for "here is where Projects live". It is the wrong shape here,
 * because most of this primer happens in Safari's share sheet and in iOS
 * Settings. Nothing rendered in our page can point at those.
 *
 * So this is self-contained: one step per page, readable without anything
 * behind it being visible, and equally usable on a desktop by somebody with a
 * phone in their other hand.
 *
 * Every word comes from `content/help/fieldPrimer.json`, which the PDF renders
 * too. Nothing is written in this file.
 */
export function FieldPrimerDialog({ open, onClose }: FieldPrimerDialogProps) {
	const pages = useMemo(() => primerPages(), [])
	// The troubleshooting table rides along as one extra page past the steps.
	const lastIndex = pages.length

	const [page, setPage] = useState(0)
	const [platform, setPlatform] = useState<PrimerPlatform>('ios')

	// Restore on OPEN rather than on mount: the dialog is mounted by its host
	// long before it is shown, and reading at mount would pin the position to
	// whatever it was when the page loaded.
	useEffect(() => {
		if (!open) return
		const stored = readProgress()
		setPage(Math.min(stored.page ?? 0, lastIndex))
		setPlatform(stored.platform ?? guessPlatform())
	}, [open, lastIndex])

	const go = useCallback(
		(next: number) => {
			const clamped = Math.max(0, Math.min(next, lastIndex))
			setPage(clamped)
			writeProgress({ page: clamped, platform })
		},
		[lastIndex, platform],
	)

	const choosePlatform = useCallback(
		(next: PrimerPlatform) => {
			setPlatform(next)
			writeProgress({ page, platform: next })
		},
		[page],
	)

	const finish = useCallback(() => {
		// Finished, so there is no place to hold. Reopening starts at the top.
		clearProgress()
		onClose()
	}, [onClose])

	const onTrouble = page === lastIndex
	const current = onTrouble ? null : pages[page]
	const progress = Math.round(((page + 1) / (lastIndex + 1)) * 100)

	return (
		<Dialog open={open} onClose={onClose}>
			{/* Overrides DialogContent's own scrolling: the footer has to stay put
			    while the body scrolls, or the reader loses Next on a long step. */}
			<DialogContent hideClose className="flex max-h-[85vh] flex-col overflow-hidden p-0">
				<div className="flex items-start justify-between gap-2 border-b border-gray-200 px-5 py-4">
					<div className="min-w-0">
						<p className="text-xs font-semibold uppercase tracking-wide text-primary">
							{onTrouble ? 'If something goes wrong' : current?.sectionTitle}
						</p>
						<h2 className="mt-0.5 truncate text-base font-semibold text-gray-900">
							{onTrouble ? 'Troubleshooting' : current?.title}
						</h2>
					</div>
					<PopupCloseButton onClose={onClose} ariaLabel="Close" className="-mr-2 -mt-2" />
				</div>

				{/* Position first, so a reader who put the phone down knows where they
				    are before they start reading again. */}
				<div className="px-5 pt-3">
					<div className="flex items-center justify-between gap-3">
						<p className="text-xs text-gray-500" aria-live="polite">
							Step {page + 1} of {lastIndex + 1}
						</p>
						{!onTrouble && (
							<div className="flex rounded-md border border-gray-200 p-0.5" role="group" aria-label="Phone">
								{(
									[
										['ios', 'iPhone'],
										['android', 'Android'],
									] as const
								).map(([value, label]) => (
									<button
										key={value}
										type="button"
										onClick={() => choosePlatform(value)}
										aria-pressed={platform === value}
										className={cn(
											'rounded px-2 py-1 text-xs font-medium transition-colors',
											platform === value
												? 'bg-primary text-primary-foreground'
												: 'text-gray-500 hover:text-gray-700',
										)}
									>
										{label}
									</button>
								))}
							</div>
						)}
					</div>
					<div className="mt-2 h-1 overflow-hidden rounded-full bg-gray-100">
						<div
							className="h-full rounded-full bg-primary transition-all duration-200"
							style={{ width: `${progress}%` }}
						/>
					</div>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
					{onTrouble ? <TroublePage /> : <PrimerPageBody page={current!} platform={platform} />}
				</div>

				<div className="flex items-center gap-2 border-t border-gray-200 px-5 py-3">
					<Button
						variant="ghost"
						isInput
						onClick={() => go(page - 1)}
						disabled={page === 0}
						className="flex-1"
					>
						<ChevronLeft className="mr-1 h-4 w-4" />
						Back
					</Button>
					{onTrouble ? (
						<Button variant="primary" isInput onClick={finish} className="flex-1">
							Done
						</Button>
					) : (
						<Button variant="primary" isInput onClick={() => go(page + 1)} className="flex-1">
							Next
							<ChevronRight className="ml-1 h-4 w-4" />
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default FieldPrimerDialog
